import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IonicModule, ToastController } from '@ionic/angular';

import { Angulartics2RouterlessModule } from 'angulartics2';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import {
  CommonUiModule,
  GlobalErrorHandler,
  PageTitleService,
  ServerTimeService,
} from '@tablesready/common-ui';
import { ApiClientModule } from '@tablesready/api-client';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { PROVIDERS } from './providers';
import { AuthenticatedLayoutComponent } from './components';
import { ConsentBannerComponent } from './consent/consent-banner.component';
import { ForbiddenHttpInterceptor } from './auth/services';
import { authAppInitializerFactory } from './auth/auth-app-initializer';
import { serverTimeAppInitializerFactory } from './services/server-time-app-initializer';
import { environment } from '../environments/environment';
import { FrontendModalModule } from '@tablesready/frontend-modal';
import { LocationSwitcherModule } from '@tablesready/frontend-location-switcher';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { FrontendHeaderModule } from '@tablesready/frontend-header';
import { ThemeSwitchModule } from '@tablesready/frontend-theme-switch';
import { NotificationCenterModule } from '@tablesready/frontend-notification-center';
import { AuthModule, AuthService, StaleTabOverlayComponent } from '@tablesready/frontend-auth';
import { AppVariables, appVariablesProvider, AppVariablesService } from '@tablesready/frontend-variables';
import { FrontendSettingsNavModule } from '@tablesready/frontend-settings-nav';
import { StorageModule } from '@tablesready/frontend-storage';
import { AlertsModule, AlertsService } from '@tablesready/frontend-alerts-bar';
import { LoggerService } from '@tablesready/frontend-logger';
import { ErrorInterceptor } from '@tablesready/common-ui';
import { FrontendGooglePlacesModule } from '@tablesready/frontend-google-places';
import { FrontendKeyboardModule } from '@tablesready/frontend-keyboard';
import { FrontendNetworkModule } from '@tablesready/frontend-network';
import { OverlayModule } from '@angular/cdk/overlay';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { FrontendChatPartyModalComponent } from '@tablesready/frontend-chat-party-modal';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent, AuthenticatedLayoutComponent, ConsentBannerComponent],
  imports: [
    ApiClientModule,
    AuthModule,
    StaleTabOverlayComponent,
    ThemeSwitchModule,
    BrowserModule,
    FrontendModalModule,
    IonicModule.forRoot({
      innerHTMLTemplatesEnabled: true,
      scrollAssist: true,
      scrollPadding: true,
    }),
    FrontendNetworkModule,
    BrowserAnimationsModule,
    CommonUiModule,
    AppRoutingModule,
    AngularSvgIconModule.forRoot(),
    Angulartics2RouterlessModule.forRoot(),
    FormsModule,
    StorageModule.forRoot(),
    LocationSwitcherModule,
    FrontendHeaderModule,
    NgbDropdownModule,
    NgbTooltipModule,
    FrontendSettingsNavModule,
    AlertsModule.forRoot(),
    FrontendGooglePlacesModule,
    FrontendKeyboardModule,
    OverlayModule,
    NotificationCenterModule,
    FrontendChatPartyModalComponent,
  ],
  providers: [
    appVariablesProvider,
    AppVariablesService,
    {
      provide: 'LOG_ERRORS',
      useValue: environment.logErrors,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ForbiddenHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (appVariablesService: AppVariablesService): (() => Promise<AppVariables>) =>
        async (): Promise<AppVariables> =>
          appVariablesService.getAppVariables().toPromise().catch(),
      multi: true,
      deps: [AppVariablesService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authAppInitializerFactory,
      multi: true,
      deps: [AuthService, AuthRestaurantService, AlertsService, LoggerService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: serverTimeAppInitializerFactory,
      multi: true,
      deps: [ServerTimeService, LoggerService],
    },
    PageTitleService,
    ...PROVIDERS,
    ToastController,
    provideCharts(withDefaultRegisterables()),
    // PrimeNG (evaluation: parallel ngrid/PrimeNG waitlist table). Aura preset,
    // dark-mode driven by the app's existing `.dark`/theme toggling via darkModeSelector.
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-theme',
        },
      },
    }),
  ],
})
export class AppModule {}
